import React, { useEffect, useState } from 'react';
import { LinkContainer } from 'react-router-bootstrap';
import { Table, Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import Message from '../components/Message';
import Loader from '../components/Loader';
import Paginate from '../components/Paginate';
import { listOrders } from '../slices/orderSlice'; // ✅ using slice
import OrderTableFilters from '../components/OrderTableFilters';
import Meta from '../components/Meta';

const OrderListScreen = ({ history, match }) => {
  const dispatch = useDispatch();

  // Viewport responsive state
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  const isMobile = viewportWidth <= 991; // card mode breakpoint (<=991px uses mobile card layout)
  const isNarrow = viewportWidth <= 480; // extra narrow tweaks

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const shortId = (id = '') => (isMobile ? id.slice(-6) : id);

  // ✅ updated to read from new orderSlice structure
  const orderState = useSelector((state) => state.order);
  const { loading, error, orders } = orderState;
  const orderRows = Array.isArray(orders) ? orders : [];

  const userState = useSelector((state) => state.user);
  const { userInfo } = userState;

  // Filters state
  const initialFilters = { id:'', user:'', dateFrom:'', dateTo:'', totalMin:'', totalMax:'', paid:'', shipped:'', delivered:'', refundPending:'', refundDenied:'' };
  const [rawFilters, setRawFilters] = useState(initialFilters);
  const [filters, setFilters] = useState(initialFilters);
  useEffect(()=>{ const t=setTimeout(()=>setFilters(rawFilters),300); return ()=>clearTimeout(t); },[rawFilters]);
  
  // Sorting state
  const [sortBy, setSortBy] = useState('date'); // 'date', 'total', 'user', 'id'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const txt = (s) => (s||'').toLowerCase().trim();
  const filteredOrders = orderRows.filter(o => {
    const f = filters;
    if (f.id && !txt(o._id).includes(txt(f.id))) return false;
    if (f.user && !txt(o.user?.username).includes(txt(f.user))) return false;
    if (f.dateFrom && new Date(o.createdAt) < new Date(f.dateFrom)) return false;
    if (f.dateTo && new Date(o.createdAt) > new Date(f.dateTo + 'T23:59:59')) return false;
    if (f.totalMin && Number(o.totalPrice) < Number(f.totalMin)) return false;
    if (f.totalMax && Number(o.totalPrice) > Number(f.totalMax)) return false;
    if (f.paid==='yes' && !o.isPaid) return false;
    if (f.paid==='no' && o.isPaid) return false;
    if (f.shipped==='yes' && !o.isShipped) return false;
    if (f.shipped==='no' && o.isShipped) return false;
    if (f.delivered==='yes' && !o.isDelivered) return false;
    if (f.delivered==='no' && o.isDelivered) return false;
    if (f.refundPending==='yes' && String(o.refundRequest?.status||'') !== 'pending') return false;
    if (f.refundPending==='no' && String(o.refundRequest?.status||'') === 'pending') return false;
    if (f.refundDenied==='yes' && String(o.refundRequest?.status||'') !== 'denied') return false;
    if (f.refundDenied==='no' && String(o.refundRequest?.status||'') === 'denied') return false;
    return true;
  });

  // Sort the filtered orders
  const sortedOrders = [...filteredOrders].sort((a, b) => {
    let compareResult = 0;
    
    switch (sortBy) {
      case 'date':
        compareResult = new Date(a.createdAt) - new Date(b.createdAt);
        break;
      case 'total':
        compareResult = Number(a.totalPrice) - Number(b.totalPrice);
        break;
      case 'user':
        const userA = (a.user?.username || '').toLowerCase();
        const userB = (b.user?.username || '').toLowerCase();
        compareResult = userA.localeCompare(userB);
        break;
      case 'id':
        compareResult = a._id.localeCompare(b._id);
        break;
      default:
        compareResult = 0;
    }
    
    return sortOrder === 'desc' ? -compareResult : compareResult;
  });

  // Client-side pagination (20 per page) driven by route param
  const PAGE_SIZE = 20;
  const routePage = Number(match.params.pageNumber || 1);
  const totalPages = Math.max(1, Math.ceil(sortedOrders.length / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, routePage), totalPages);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const paginatedOrders = sortedOrders.slice(startIndex, startIndex + PAGE_SIZE);

  useEffect(() => {
    if (userInfo && userInfo.isAdmin) {
      dispatch(listOrders());
    } else {
      history.push('/login');
    }
  }, [dispatch, history, userInfo]);

  return (
    <>
  <Meta title='Admin — Orders — The Lotustor' robots='noindex,follow' />
      <h1>Orders</h1>
      <OrderTableFilters value={rawFilters} onChange={setRawFilters} onClear={()=>setRawFilters(initialFilters)} />
      
      {/* Sorting Controls */}
      <div className='d-flex align-items-center gap-3 mb-3 flex-wrap'>
        <label className='mb-0 fw-bold'>Sort by:</label>
        <select 
          className='form-select form-select-sm' 
          style={{ width: 'auto' }}
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
        >
          <option value='date'>Date</option>
          <option value='total'>Total Price</option>
          <option value='user'>User Name</option>
          <option value='id'>Order ID</option>
        </select>
        
        <Button
          size='sm'
          variant='outline-secondary'
          onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
        >
          <i className={`fas fa-sort-amount-${sortOrder === 'desc' ? 'down' : 'up'}`}></i>
          {' '}{sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
        </Button>
      </div>
      
      <div className='small text-muted mb-2'>
        Showing {sortedOrders.length ? `${startIndex + 1}–${Math.min(startIndex + PAGE_SIZE, sortedOrders.length)}` : 0} of {sortedOrders.length} (Page {currentPage} of {totalPages})
      </div>
      {loading ? (
        <Loader />
      ) : error ? (
        <Message variant='danger'>{error}</Message>
      ) : (
        <>
          <Table
            striped
            bordered
            hover
            responsive
            aria-label='Orders list'
            className={`table-sm table-orders ${
              isMobile ? 'mobile-mode' : ''
            }`}
          >
            {!isMobile && (
              <thead>
                <tr>
                  <th>ID</th>
                  <th>USER</th>
                  <th>DATE</th>
                  <th>TOTAL</th>
                  <th>PAID</th>
                  <th>SHIPPED</th>
                  <th>DELIVERED</th>
                  <th>GET DETAILS</th>
                </tr>
              </thead>
            )}
            <tbody>
              {paginatedOrders.map((order) => {
              const paidIcon = order.isPaid ? (
                <>
                  <i
                    className='fas fa-check text-success'
                    aria-hidden='true'
                  ></i>
                  <span className='visually-hidden'>Paid</span>
                  {!isMobile && ' ' + order.paidAt.substring(0, 10)}
                </>
              ) : (
                <>
                  <i
                    className='fas fa-times text-danger'
                    aria-hidden='true'
                  ></i>
                  <span className='visually-hidden'>Not Paid</span>
                </>
              );
              const shippedIcon = order.isShipped ? (
                <>
                  <i
                    className='fas fa-check text-success'
                    aria-hidden='true'
                  ></i>
                  <span className='visually-hidden'>Shipped</span>
                  {!isMobile && ' ' + order.shippedAt.substring(0, 10)}
                </>
              ) : (
                <>
                  <i
                    className='fas fa-times text-danger'
                    aria-hidden='true'
                  ></i>
                  <span className='visually-hidden'>Not Shipped</span>
                </>
              );
              const deliveredIcon = order.isDelivered ? (
                <>
                  <i
                    className='fas fa-check text-success'
                    aria-hidden='true'
                  ></i>
                  <span className='visually-hidden'>Delivered</span>
                  {!isMobile && ' ' + order.deliveredAt.substring(0, 10)}
                </>
              ) : (
                <>
                  <i
                    className='fas fa-times text-danger'
                    aria-hidden='true'
                  ></i>
                  <span className='visually-hidden'>Not Delivered</span>
                </>
              );
              return (
                <tr key={order._id}>
                  <td data-label='ID'>{shortId(order._id)}</td>
                  <td data-label='USER'>{order.user && order.user.username}</td>
                  {!isMobile && <td data-label='DATE'>{order.createdAt.substring(0, 10)}</td>}
                  <td data-label='TOTAL'>${order.totalPrice}</td>
                  {isMobile && isNarrow ? (
                    <td data-label='STATUS' style={{ display: 'flex', gap: '0.75rem' }}>
                      <span title='Paid'>{paidIcon}</span>
                      <span title='Shipped'>{shippedIcon}</span>
                      <span title='Delivered'>{deliveredIcon}</span>
                    </td>
                  ) : (
                    <>
                      <td data-label='PAID'>{paidIcon}</td>
                      <td data-label='SHIPPED'>{shippedIcon}</td>
                      <td data-label='DELIVERED'>{deliveredIcon}</td>
                    </>
                  )}
                  <td data-label='DETAILS'>
                    <LinkContainer to={`/order/${order._id}`}>
                      <Button variant='light' className='btn-sm'>
                        Details
                        {isMobile && (
                          <span className='visually-hidden'> for order {order._id}</span>
                        )}
                      </Button>
                    </LinkContainer>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <Paginate pages={totalPages} page={currentPage} isAdmin={true} keyword='' baseUrl='/admin/orderlist' />
        </>
      )}
    </>
  );
};

export default OrderListScreen;
